import IconClose from '@apollo/icons/default/IconClose.svg';
import { IconButton } from '@apollo/orbit';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';

import { useExplorerState } from 'src/app/graph/explorerPage/hooks/useExplorerState/useExplorerState';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  MONACO_READONLY_OPTIONS,
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';

import { useThemeContext } from '../../../../../components/themeProvider/ThemeProvider';
import { ExplorerBehavioralFlags } from '../../explorerBehavioralFlags';
import { applyClassNameToRanges } from '../../helpers/monacoHelpers';
import { SubscriptionResponses } from '../../hooks/useExplorerState/useSubscriptions';
import { SubscriptionsStatus } from '../subscriptionStatus/SubscriptionStatus';
import { useNetworkErrorToast } from '../useNetworkErrorToast';

const serializeSubscriptionResponses = (responses: SubscriptionResponses) =>
  responses
    .map((r) =>
      r.isSeparator
        ? `\n// Reconnected at ${r.timestamp}\n`
        : `\n// ${r.isError ? 'Error' : 'Response'} received at ${
            r.timestamp
          }\n${JSON.stringify(r.data, null, 2)}`,
    )
    .join('\n');

export const SubscriptionResponsePane = ({
  openEditUrlModal,
  subscription,
  subscriptionUrl,
  explorerBehavioralFlags,
  onCloseSubscriptionResponses,
}: {
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
  subscription: ReturnType<typeof useExplorerState>['subscription'];
  subscriptionUrl: string | null;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  onCloseSubscriptionResponses: () => void;
}) => {
  const [showNetworkWarning, setShowNetworkWarning] = useState(false);
  const { showNetworkErrorToast, hideNetworkErrorToast } = useNetworkErrorToast(
    {
      setShowNetworkWarning,
      openEditUrlModal,
      triggeredBySubscription: true,
      introspectionDisabled: false,
      url: subscriptionUrl,
    },
  );

  const responses = subscription.subscriptionResponse;
  const serializedSubscriptionResults = useMemo(() => {
    const firstSeparatorIndex = _.findIndex(
      responses,
      ({ isSeparator }) => !!isSeparator,
    );
    const hasSeparator = firstSeparatorIndex >= 0;
    return {
      current: serializeSubscriptionResponses(
        hasSeparator ? responses.slice(0, firstSeparatorIndex + 1) : responses,
      ),
      past: hasSeparator
        ? serializeSubscriptionResponses(
            responses.slice(firstSeparatorIndex + 1),
          )
        : '',
    };
  }, [responses]);

  const subscriptionResponseEditor = useStudioEditor({
    language: 'json',
    ...MONACO_READONLY_OPTIONS,
  });

  // Sets editor text when responses update, when updating through the `src` prop
  // it selects all text in the editor
  useEffect(() => {
    subscriptionResponseEditor.setValue(
      (subscription.socketError
        ? JSON.stringify(subscription.socketError, ['message'], 2)
        : serializedSubscriptionResults.current +
          serializedSubscriptionResults.past) ?? '',
    );
    subscriptionResponseEditor.setScrollPosition({ scrollTop: 0 });

    if (subscription.socketError) return;

    const pastResultsRanges = [
      monacoEditor.Range.fromPositions(
        new monacoEditor.Position(
          (serializedSubscriptionResults.current || '').split('\n').length + 2,
          1,
        ),
        subscriptionResponseEditor
          .getModel()
          ?.getFullModelRange()
          .getEndPosition(),
      ),
    ];
    applyClassNameToRanges(
      'opacity-50',
      subscriptionResponseEditor,
      pastResultsRanges,
    );
  }, [
    serializedSubscriptionResults,
    subscriptionResponseEditor,
    subscription.socketError,
  ]);

  const { themeName, themedValue } = useThemeContext();

  useEffect(() => {
    if (subscription.socketError) {
      setShowNetworkWarning(true);
    } else {
      setShowNetworkWarning(false);
    }
  }, [
    explorerBehavioralFlags.shouldPostMessageOperations,
    subscription.socketError,
  ]);

  useEffect(() => {
    if (showNetworkWarning) {
      showNetworkErrorToast();
    } else {
      hideNetworkErrorToast();
    }
  }, [
    openEditUrlModal,
    showNetworkWarning,
    subscription.subscriptionResponse,
    themeName,
    themedValue,
    showNetworkErrorToast,
    hideNetworkErrorToast,
  ]);

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-none flex-wrap items-center justify-between whitespace-nowrap border-b border-primary pb-3 text-primary">
        <div className="flex items-center pr-4 text-base font-semibold">
          <Tooltip label="Stop listening and close">
            <IconButton
              aria-label="Stop listening to subscription and close panel"
              size="sm"
              variant="hidden"
              onClick={onCloseSubscriptionResponses}
              icon={<IconClose />}
            />
          </Tooltip>
          Subscriptions
        </div>
        {explorerBehavioralFlags.showResultStatus && (
          <SubscriptionsStatus
            socketStatus={subscription.socketStatus}
            stopListening={subscription.stopListening}
            isListening={subscription.isListening}
            subscriptionUrl={subscriptionUrl}
          />
        )}
      </div>
      <div className="relative flex h-0 w-full flex-1 overflow-hidden">
        <StudioEditorContainer
          editor={subscriptionResponseEditor}
          className="h-full"
        />
      </div>
    </div>
  );
};
